import React, { MutableRefObject, useCallback, useContext, useEffect, useRef } from 'react';
import io, { Socket } from 'socket.io-client';
import {
  FullMessageType,
  PostMessageType,
  PostSocialPayloadType,
  SocialMessageType,
  postMessageTypeEnum,
  wsMessageTypeEnum,
} from './useWebsocketTypes';

import { DefaultEventsMap } from '@socket.io/component-emitter';
import { useAppSelector } from 'hooks/hooks';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { setBrowserOnline } from 'redux/actions/userActions';
import config from 'shared/config';
import { PostGetCommentPayload } from 'types/postTypes';
import { RootState } from 'types/stateTypes';
import { handleMessage } from './handleMessage';
import { handlePostComment } from './handlePostComment';
import { handleSocial } from './handleSocial';
import { generatePostPayload } from './postMessage';

const SocketContext = React.createContext<ReturnType<typeof io> | unknown>({});

type Props = {
  children: PropTypes.ReactNodeLike;
};

export const WebsocketProvider = ({ children }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const userId = useAppSelector((state: RootState) => state.auth.id);
  const selectedChatId = useAppSelector((state: RootState) => state.chats.selectedChatId);
  const chatsData = useAppSelector((state: RootState) => state.chats.chats);
  const callId = useAppSelector((state: RootState) => state.call.dialogId);
  const mainUserRef: MutableRefObject<Socket<DefaultEventsMap, DefaultEventsMap> | null> = useRef(null);
  const currentChatRef: MutableRefObject<typeof selectedChatId> = useRef(selectedChatId);
  const { current: mainUser } = mainUserRef;
  currentChatRef.current = selectedChatId;

  const handleMessages = (fullMessage: FullMessageType) => {
    handleMessage({
      callId,
      chatsData,
      fullMessage,
      selectedChatId: currentChatRef.current,
      userId,
    });
  };

  const handleSocials = (fullMessage: SocialMessageType) => {
    handleSocial({
      fullMessage,
    });
  };

  const handleNotification = (payload: PostGetCommentPayload) => {
    handlePostComment(payload);
  };

  const handleConnect = useCallback(() => {
    dispatch(setBrowserOnline(true));
  }, [dispatch]);

  const handleDisconnect = useCallback(() => {
    dispatch(setBrowserOnline(false));
  }, [dispatch]);

  useEffect(() => {
    if (mainUserRef.current) {
      mainUserRef.current.off(postMessageTypeEnum.message, handleMessages);
      mainUserRef.current.off(postMessageTypeEnum.social, handleSocials);
      mainUserRef.current.off(postMessageTypeEnum.ntf, handleNotification);
      mainUserRef.current.off(postMessageTypeEnum.connect, handleConnect);
      mainUserRef.current.off(postMessageTypeEnum.disconnect, handleDisconnect);
      mainUserRef.current.close();
    }

    if (!userId) return;

    const ws = io(`${config.webSocketUrl}`, {
      path: '/api/chat/socket.io/',
      withCredentials: true,
    });

    ws.on(postMessageTypeEnum.message, handleMessages);
    ws.on(postMessageTypeEnum.social, handleSocials);
    ws.on(postMessageTypeEnum.ntf, handlePostComment);
    ws.on(postMessageTypeEnum.connect, handleConnect);
    ws.on(postMessageTypeEnum.disconnect, handleDisconnect);

    mainUserRef.current = ws;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return <SocketContext.Provider value={mainUser}>{children}</SocketContext.Provider>;
};

export const useWebsocket = (): { postMessage: (postMessage: PostMessageType | PostSocialPayloadType) => void } => {
  const mainUser = useContext(SocketContext) as unknown as ReturnType<typeof io>;

  const postMessage = useCallback(
    ({
      cType = wsMessageTypeEnum.textMessage,
      dialogs,
      forwarded,
      forwardedn,
      interlocutor,
      messageText,
      pollId,
      selectedChatId,
      uploadFiles,
      users,
      messageType = postMessageTypeEnum.message,
      rObjects,
      type,
      answered,
    }: PostMessageType) => {
      if (!mainUser) return;
      const payload = generatePostPayload({
        answered,
        cType,
        dialogs,
        forwarded,
        forwardedn,
        interlocutor,
        messageText,
        messageType,
        pollId,
        rObjects,
        selectedChatId,
        type,
        uploadFiles,
        users,
      });
      mainUser.emit(messageType, payload);
    },
    [mainUser]
  );

  return {
    postMessage,
  };
};
